import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { useTeamStore } from '@/store/useTeamStore';
import { useChatStore } from '@/store/useChatStore';
import { authClient } from '@/lib/authClient';
import { api } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { LoginDialog } from '@/components/auth/LoginDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Ticket, User, UserCircle, Megaphone, ChevronDown } from 'lucide-react';

export function UserSettings() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const setIsOperator = useAuthStore((s) => s.setIsOperator);
  const hasImplicitAccess = useAuthStore((s) => s.hasImplicitAccess);
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const [loginOpen, setLoginOpen] = useState(false);

  const [lifeContext, setLifeContext] = useState('');
  const [workContext, setWorkContext] = useState('');
  const [currentFocus, setCurrentFocus] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    setLifeContext(profile.lifeContext);
    setWorkContext(profile.workContext);
    setCurrentFocus(profile.currentFocus);
  }, [profile.lifeContext, profile.workContext, profile.currentFocus]);

  type VoucherStatus =
    | { active: false }
    | {
        active: true;
        code: string;
        usedCount: number;
        usageLimit: number;
        scope: 'per_user' | 'global';
        totalUsed: number;
        expiresAt: string | null;
        revokedAt: string | null;
      };

  const [voucherStatus, setVoucherStatus] = useState<VoucherStatus | null>(null);
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [redeemFormOpen, setRedeemFormOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.vouchers.status().then(setVoucherStatus).catch(console.error);
  }, [user]);

  async function handleRedeemVoucher() {
    if (!voucherInput.trim()) return;
    setVoucherLoading(true);
    try {
      const res = await api.vouchers.redeem(voucherInput.trim());
      setVoucherStatus({
        active: true,
        code: res.code,
        usedCount: res.usedCount,
        usageLimit: res.usageLimit,
        scope: res.scope,
        totalUsed: 0, // fresh redemption
        expiresAt: null,
        revokedAt: null,
      });
      setVoucherInput('');
      setRedeemFormOpen(false);
      toast.success('Gutschein eingelöst!');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Einlösen fehlgeschlagen';
      toast.error(msg);
    } finally {
      setVoucherLoading(false);
    }
  }

  async function handleSaveProfile() {
    setProfileSaving(true);
    try {
      await api.me.updateProfile({
        lifeContext: lifeContext.trim(),
        workContext: workContext.trim(),
        currentFocus: currentFocus.trim(),
      });
      setProfile({
        lifeContext: lifeContext.trim(),
        workContext: workContext.trim(),
        currentFocus: currentFocus.trim(),
      });
      toast.success('Gespeichert');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setProfileSaving(false);
    }
  }

  const profileDirty =
    lifeContext.trim() !== profile.lifeContext ||
    workContext.trim() !== profile.workContext ||
    currentFocus.trim() !== profile.currentFocus;

  type AnnouncementArchive = Awaited<ReturnType<typeof api.announcements.list>>;
  const [announcements, setAnnouncements] = useState<AnnouncementArchive | null>(null);
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);

  useEffect(() => {
    if (!user || !announcementsOpen || announcements !== null) return;
    api.announcements.list().then(setAnnouncements).catch(console.error);
  }, [user, announcementsOpen, announcements]);

  async function handleSignOut() {
    // Belt-and-suspenders: best-effort push of local data before clearing.
    // Most data is already synced via per-action API calls; this catches
    // anything that quietly failed. A failure here must NOT block logout —
    // trapping the user in the app over a sync hiccup is worse than the
    // (rare) data loss.
    try {
      const localMembers = useTeamStore.getState().members;
      const localSessions = useChatStore.getState().sessions;
      if (localMembers.length > 0 || localSessions.length > 0) {
        await api.sync.push({ members: localMembers, sessions: localSessions });
      }
    } catch (err) {
      console.warn('[signout] best-effort sync failed, proceeding with logout', err);
      toast.warning('Hinweis: Letzte Änderungen konnten nicht synchronisiert werden.');
    }

    await authClient.signOut();
    setUser(null);
    setIsOperator(false);
    setProfile({ lifeContext: '', workContext: '', currentFocus: '' });
    setVoucherStatus(null);
    setRedeemFormOpen(false);
    setVoucherInput('');

    // Clear local stores so the next login starts clean
    useTeamStore.getState().clearLocal();
    useChatStore.getState().clearLocal();

    toast.success('Abgemeldet');
  }

  return (
    <AppShell>
      <PageHeader title="Einstellungen" />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-8">

        {/* Account section */}
        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold font-sans">Konto</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Melde dich an, um deine Daten zu sichern und auf anderen Geräten zuzugreifen.
            </p>
          </div>
          {user ? (
            <div className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
              <User size={18} className="text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={handleSignOut}>
                <LogOut size={14} className="mr-1" />
                Abmelden
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setLoginOpen(true)}>
              Anmelden
            </Button>
          )}
        </section>

        {user && !hasImplicitAccess && (
          <section className="space-y-4 border-t border-border pt-6">
            <div>
              <h2 className="text-base font-semibold font-sans flex items-center gap-2">
                <Ticket size={18} /> Gutschein
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Löse einen Gutschein-Code ein, um das Innere Team kostenlos zu nutzen.
              </p>
            </div>
            {voucherStatus?.active && (
              <div className="rounded-xl border border-border px-4 py-3 space-y-1">
                <p className="text-sm font-medium">Code: <span className="font-mono">{voucherStatus.code}</span></p>
                <p className="text-xs text-muted-foreground">
                  {voucherStatus.scope === 'global'
                    ? `${voucherStatus.totalUsed} von ${voucherStatus.usageLimit} Nachrichten (gemeinsames Budget)`
                    : `${voucherStatus.usedCount} von ${voucherStatus.usageLimit} Nachrichten verbraucht`}
                </p>
              </div>
            )}

            {voucherStatus?.active && !redeemFormOpen && (
              <button
                type="button"
                onClick={() => setRedeemFormOpen(true)}
                className="text-xs text-primary underline underline-offset-2"
              >
                Anderen Code einlösen
              </button>
            )}

            {(!voucherStatus?.active || redeemFormOpen) && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={voucherInput}
                    onChange={(e) => setVoucherInput(e.target.value)}
                    placeholder="Code eingeben"
                    className="font-mono text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleRedeemVoucher()}
                  />
                  <Button onClick={handleRedeemVoucher} disabled={voucherLoading || !voucherInput.trim()}>
                    Einlösen
                  </Button>
                  {voucherStatus?.active && (
                    <Button variant="ghost" onClick={() => { setRedeemFormOpen(false); setVoucherInput(''); }}>
                      Abbrechen
                    </Button>
                  )}
                </div>
                {voucherStatus?.active && (
                  <p className="text-xs text-muted-foreground">
                    Der neue Code ersetzt deinen aktuellen — das Kontingent startet wieder bei 0.
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {user && (
          <section className="space-y-4 border-t border-border pt-6">
            <div>
              <h2 className="text-base font-semibold font-sans flex items-center gap-2">
                <UserCircle size={18} /> Über dich
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Optional. Gib dem Entdeckungs-Chat Kontext über dich, damit die Erkundung gezielter wird.
                Deine Eingaben werden verschlüsselt gespeichert und nie an andere weitergegeben.
              </p>
            </div>

            <label className="block space-y-1">
              <span className="text-sm font-medium">Lebenssituation</span>
              <Textarea
                value={lifeContext}
                onChange={(e) => setLifeContext(e.target.value)}
                placeholder="z.B. Familie, Wohnort, wichtige Beziehungen"
                rows={3}
                className="text-sm resize-none"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-sm font-medium">Berufliche Situation</span>
              <Textarea
                value={workContext}
                onChange={(e) => setWorkContext(e.target.value)}
                placeholder="z.B. Job, Branche, typische Herausforderungen"
                rows={3}
                className="text-sm resize-none"
              />
            </label>

            <label className="block space-y-1">
              <span className="text-sm font-medium">Aktuelle Themen</span>
              <Textarea
                value={currentFocus}
                onChange={(e) => setCurrentFocus(e.target.value)}
                placeholder="Was beschäftigt dich gerade?"
                rows={3}
                className="text-sm resize-none"
              />
            </label>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveProfile}
                disabled={!profileDirty || profileSaving}
              >
                {profileSaving ? 'Speichere…' : 'Speichern'}
              </Button>
            </div>
          </section>
        )}

        {user && (
          <section className="space-y-3 border-t border-border pt-6">
            <div>
              <h2 className="text-base font-semibold font-sans flex items-center gap-2">
                <Megaphone size={18} /> Was ist neu
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Alle bisherigen Ankündigungen.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAnnouncementsOpen((v) => !v)}
              className="flex items-center justify-between gap-2 w-full rounded-xl border border-border px-4 py-3 hover:bg-muted transition-colors"
            >
              <span className="text-sm font-medium">
                {announcementsOpen ? 'Liste schließen' : 'Liste anzeigen'}
              </span>
              <ChevronDown
                size={16}
                className={`text-muted-foreground shrink-0 transition-transform ${announcementsOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {announcementsOpen && (
              <div className="space-y-2">
                {announcements === null ? (
                  <p className="text-sm text-muted-foreground">Lade…</p>
                ) : announcements.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Noch keine Ankündigungen.</p>
                ) : (
                  announcements.map((a) => (
                    <article key={a.id} className="rounded-xl border border-border px-4 py-3 space-y-1">
                      <div>
                        <p className="text-sm font-semibold">{a.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(a.publishedAt).toLocaleDateString('de-DE', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <p className="text-sm text-foreground/80 whitespace-pre-line">{a.body}</p>
                      {a.linkHref && a.linkLabel && (
                        a.linkHref.startsWith('/') ? (
                          <Link to={a.linkHref} className="inline-block text-xs text-primary underline underline-offset-2">
                            {a.linkLabel} →
                          </Link>
                        ) : (
                          <a
                            href={a.linkHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-xs text-primary underline underline-offset-2"
                          >
                            {a.linkLabel} →
                          </a>
                        )
                      )}
                    </article>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        <section className="space-y-2 border-t border-border pt-6">
          <h2 className="text-base font-semibold font-sans">Über die App</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            "Meet Your Inner Team" basiert auf dem psychologischen Modell des "Inneren Teams" von
            Friedemann Schulz von Thun. Es hilft Menschen dabei, die verschiedenen inneren Stimmen und
            Persönlichkeitsanteile zu entdecken und zu verstehen.
          </p>
        </section>
      </div>

      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </AppShell>
  );
}
